import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIsMobile } from '../lib/media';
import { CrumbContext, type Crumb } from './crumbs';
import { useNavLock } from './lock';
import { appNav } from './nav';

/*
 * Хост стопки: одно и то же дерево показывает двумя способами.
 *
 * Десктоп: обёртки .nav-root/.nav-layer — display:contents, содержимое
 * попадает прямо в сетку раздела, как раньше; из слоёв, кроме kind="chat",
 * виден только верхний (CSS), нижние живут смонтированными.
 *
 * Телефон: слой — position:fixed во весь экран, приезжает справа; тот, что
 * под ним, уезжает на 22% влево и притемняется. Слои ниже вершины получают
 * inert — по ним нельзя ни тапнуть, ни проскроллить.
 */

export type HostLayer = { key: string; kind: string; node: ReactNode };

/** Сколько слой уезжает, прежде чем его можно снять с экрана (чуть больше перехода). */
const EXIT_MS = 300;

/**
 * `pages` — стопка страниц раздела: на десктопе виден только верхний экран,
 * корень прячется (как раньше, когда разделы переключались целиком). Без
 * него корень остаётся на месте (список чатов рядом с перепиской).
 */
export function StackHost({ root, layers, pages }: { root: ReactNode; layers: HostLayer[]; pages?: boolean }) {
  const mobile = useIsMobile();
  // пока открыт слой на телефоне, страница заперта от прокрутки, а слой
  // подгоняется под видимую область (клавиатура) — см. lock.ts
  useNavLock(mobile && layers.length > 0);
  // Снятый слой держим ещё EXIT_MS, чтобы он успел уехать вправо.
  const [leaving, setLeaving] = useState<HostLayer[]>([]);
  const prev = useRef<HostLayer[]>([]);

  useEffect(() => {
    const keys = new Set(layers.map((l) => l.key));
    const gone = prev.current.filter((l) => !keys.has(l.key));
    prev.current = layers;
    if (!gone.length) return;
    setLeaving((cur) => [...cur.filter((l) => !keys.has(l.key)), ...gone]);
    const t = setTimeout(
      () => setLeaving((cur) => cur.filter((l) => !gone.some((g) => g.key === l.key))),
      EXIT_MS,
    );
    return () => clearTimeout(t);
  }, [layers]);

  /*
   * Хлебные крошки: экран называет себя, хост раздаёт каждому слою путь из
   * заголовков тех, кто под ним (3.64). Корень снимается к разделу, слой —
   * до себя.
   */
  const [titles, setTitles] = useState<Record<string, string>>({});
  const setters = useRef(new Map<string, (title: string) => void>());
  const setterOf = (key: string) => {
    let set = setters.current.get(key);
    if (!set) {
      set = (title: string) => setTitles((was) => (was[key] === title ? was : { ...was, [key]: title }));
      setters.current.set(key, set);
    }
    return set;
  };
  const trailTo = (index: number): Crumb[] =>
    ['root', ...layers.slice(0, index).map((l) => l.key)]
      .filter((key) => titles[key])
      .map((key) => ({
        title: titles[key],
        // Стопку берём в момент нажатия: хосты, у которых её нет вовсе
        // (стенды и тесты без App), крошек и не покажут — пути у них нет.
        onClick: key === 'root' ? () => appNav().reset() : () => appNav().backTo(key),
      }));

  const top = layers.length ? layers[layers.length - 1].key : null;
  // React 18 не знает атрибут inert — отдаём его через spread
  const inert = (on: boolean) => (mobile && on ? { inert: '' } : {}) as Record<string, string>;

  return (
    <>
      <div className={`nav-root${pages ? ' pages' : ''}${layers.length ? ' under' : ''}`} {...inert(layers.length > 0)}>
        <CrumbContext.Provider value={{ trail: [], set: setterOf('root') }}>{root}</CrumbContext.Provider>
      </div>
      {layers.map((l, i) => (
        <LayerBox
          key={l.key}
          kind={l.kind}
          top={l.key === top}
          mobile={mobile}
          {...inert(l.key !== top)}
        >
          <CrumbContext.Provider value={{ trail: trailTo(i), set: setterOf(l.key) }}>{l.node}</CrumbContext.Provider>
        </LayerBox>
      ))}
      {leaving.map((l) => (
        <div key={`x:${l.key}`} className="nav-layer exit" data-kind={l.kind} {...inert(true)}>
          {l.node}
        </div>
      ))}
    </>
  );
}

function LayerBox({
  kind,
  top,
  mobile,
  children,
  ...rest
}: {
  kind: string;
  top: boolean;
  mobile: boolean;
  children: ReactNode;
} & Record<string, unknown>) {
  const ref = useRef<HTMLDivElement>(null);
  const [fresh, setFresh] = useState(true);

  // Въезд: смонтировались за правым краем (класс enter), заставили браузер
  // это посчитать (offsetWidth) и сняли класс — получился переход, а не
  // прыжок. Не через requestAnimationFrame: в неактивной вкладке кадры не
  // рисуются, и слой остался бы за краем навсегда.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    void el.offsetWidth;
    setFresh(false);
    if (mobile) el.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
    // только при монтировании
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={ref}
      className={`nav-layer${fresh ? ' enter' : ''}`}
      data-kind={kind}
      {...(top ? { 'data-top': '' } : {})}
      {...rest}
    >
      {children}
    </div>
  );
}
